import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from "react-router";

import { IReportDataObject, IReportData } from '../../../entities/reports/IReportData';
import { IData as IClientDetail, ISaveData } from "../../../store/client/clientItem";
import { IData as IClientFile } from "../../../store/client/clientFilesList";
import { FetchState } from '../../../entities/FetchState';
import { INavigations, mapNavigationsToProps, ClientNavigation, ClientNavigationPage } from '../../organisms/client/ClientNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { ClientEdit } from '../../organisms/client/ClientEdit';
import { SaveButton } from '../../atoms/buttons/SaveButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import { MarkeerAlsOverledenButton } from '../../atoms/buttons/MarkeerAlsOverledenButton';
import { MarkeerAlsLevendButton } from '../../atoms/buttons/MarkeerAlsLevendButton';
import ErrorMessage from '../../organisms/ErrorMessage';
import { SavingMessage } from '../../organisms/SavingMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';
import { UnsavedChangesBar } from '../../organisms/UnsavedChangesBar';

// Interface for Route Parameters
interface IMatchParams {
    id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State
interface IStateProps {
    details: IReportDataObject<IClientDetail>
    saver: IReportDataObject<IClientDetail>
    deleter: IReportDataObject<IClientDetail>
    markDeceasedItem: IReportDataObject<IClientDetail>
    unmarkDeceasedItem: IReportDataObject<IClientDetail>
    clientFiles: IReportData<IClientFile>
    contactPersonTypes: Array<{ streamId: string; name: string }>
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    details: state.client.clientItem.Item,
    saver: state.client.clientItem.SaveItem,
    deleter: state.client.clientItem.DeleteItem,
    markDeceasedItem: state.client.clientItem.MarkDeceasedItem,
    unmarkDeceasedItem: state.client.clientItem.UnmarkDeceasedItem,
    clientFiles: state.client.clientFilesList.ClientFilesList,
    contactPersonTypes: state.contactpersontypes.List.List.data || []
});

// Outgoing functions to Dispatch
interface IDispatchProps {
    navigations: INavigations;
    onLoadDetail: (id: string) => void;
    onSave: (streamId: string, details: ISaveData) => void;
    onDelete: (id: string) => void;
    onMarkDeceased: (streamId: string) => void;
    onUnmarkDeceased: (streamId: string) => void;
    onResetDeleteState: () => void;
    onResetSaveState: () => void;
    onLoadFiles: (clientId: string) => void;
    onLoadContactPersonTypes: () => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    navigations: mapNavigationsToProps(dispatch),
    onLoadDetail: (id: string) => dispatch(RootActionCreators.Client.clientItem.FetchData(id)),
    onSave: (streamId: string, details: ISaveData) => dispatch(RootActionCreators.Client.clientItem.SaveData(streamId, details)),
    onDelete: (id: string) => dispatch(RootActionCreators.Client.clientItem.DeleteData(id)),
    onMarkDeceased: (streamId: string) => dispatch(RootActionCreators.Client.clientItem.MarkDeceasedData(streamId)),
    onUnmarkDeceased: (streamId: string) => dispatch(RootActionCreators.Client.clientItem.UnmarkDeceasedData(streamId)),
    onResetDeleteState: () => dispatch(RootActionCreators.Client.clientItem.ResetDeleteState()),
    onResetSaveState: () => dispatch(RootActionCreators.Client.clientItem.ResetSaveState()),
    onLoadFiles: (clientId: string) => dispatch(RootActionCreators.Client.clientFilesList.FetchData(clientId)),
    onLoadContactPersonTypes: () => dispatch(RootActionCreators.ContactPersonTypes.List.FetchData())
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const ClientEditPage = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const { t: tCommon } = useTranslation('common');

    const isError = props.details.state === FetchState.Error;
    const isLoading = props.details.state === FetchState.NotFetched || props.details.state === FetchState.Busy;

    const [details, setDetails] = React.useState(props.details.data);
    const [saveData, setSaveData] = React.useState<ISaveData | null>(null);
    const [isValid, setIsValid] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

    // Reset unsaved changes indicator after a successful save
    React.useEffect(() => {
        if (props.saver.state === FetchState.Success) {
            setHasUnsavedChanges(false);
        }
    }, [props.saver.state]);
    
    // Load contact person types on mount
    React.useEffect(() => {
        props.onLoadContactPersonTypes();
    }, []);

    // Load data when component mounts
    React.useEffect(() => {
        const id = props.match.params.id;
        if (props.details.state === FetchState.NotFetched) {
            setIsValid(false);
            props.onLoadDetail(id);
            props.onLoadFiles(id); // Load files for this client
        } else if (props.details.state === FetchState.Success) {
            if (props.details.data.streamId !== id) {
                setIsValid(false);
                props.onLoadDetail(id);
                props.onLoadFiles(id); // Load files for this client
            }
        }
        // Update local state when data successfully loads
        if (props.details.state === FetchState.Success && props.details.data) {
            setIsValid(true);
            setDetails(props.details.data);
        }
    }, [props.details.state, props.match.params.id, props.details?.data?.streamId]);

    // Convert IData to ISaveData for the form - memoized to prevent unnecessary re-renders
    const initialData = React.useMemo((): Partial<ISaveData> => ({
        firstName: details.firstName,
        lastName: details.lastName,
        burgerServiceNumber: details.burgerServiceNumber,
        dateOfBirth: details.birthDate ? details.birthDate.toISOString() : null,
        gender: details.gender,
        officialStreet: details.officialStreet || '',
        residencyStreet: details.residencyStreet,
        officialStreetNumber: details.officialStreetNumber || '',
        residencyStreetNumber: details.residencyStreetNumber,
        homePhone: details.homePhone,
        workPhone: details.workPhone,
        mobilePhone: details.mobilePhone,
        email: details.email,
        contactPerson: details.contactPerson,
        contactPersons: details.contactPersons || [],
        remarks: details.remarks,
        disabilityStatus: (details.disabilityConfirmed ? 'Confirmed' : details.disabilitySuspected ? 'Suspected' : 'None') as 'Confirmed' | 'Suspected' | 'None',
        languageStreamId: details.languageId,
        residencyCityStreamId: details.residencyCityId,
        officialCityStreamId: details.officialCityId || '',
        activityTypeStreamId: details.activityTypeId,
        disabilityId: details.disabilityTypeIds || [],
        problemId: details.problemIds || []
    }), [details]); // Recalculate when details changes (including after loading)

    const errorMessages = [props.details.errorMessage].filter(Boolean).join(', ');
    const hasFiles = props.clientFiles.state === FetchState.Success && props.clientFiles.data.length > 0;

    return (
        <ClientNavigation navigations={props.navigations} selected={ClientNavigationPage.Edit}>
            <PageHeader
                title={tClient('editTitle', { fullName: details.fullName || '' })}
                subTitle={tClient('editSubtitle')}
                explainText={tClient('editExplanation')}
            />

            <UnsavedChangesBar show={hasUnsavedChanges} />

            <ClientEdit
                streamId={details.streamId}
                initialData={initialData}
                loading={isLoading}
                isEditMode={true}
                contactPersonTypes={props.contactPersonTypes}
                onChange={(data, valid) => {
                    setSaveData(data);
                    setIsValid(valid);
                }}
                onDirtyChange={() => setHasUnsavedChanges(true)}
            />

            <RefreshButton 
                onClick={() => props.onLoadDetail(props.match.params.id)} 
                disabled={props.details.state === FetchState.Busy} 
            />
            <SaveButton 
                disabled={!isValid || isLoading} 
                onClick={() => saveData && props.onSave(props.match.params.id, saveData)} 
            />
            <DeleteButton 
                disabled={isLoading || hasFiles} 
                onClick={() => props.onDelete(props.match.params.id)} 
                deleteWarningMessage={hasFiles 
                    ? tClient('cannotDeleteHasFiles')
                    : tClient('deleteWarning', { fullName: details.fullName })
                } 
            />
            
            {details.isDeceased ? (
                <MarkeerAlsLevendButton 
                    disabled={isLoading} 
                    onClick={() => props.onUnmarkDeceased(props.match.params.id)} 
                />
            ) : (
                <MarkeerAlsOverledenButton 
                    disabled={isLoading} 
                    onClick={() => props.onMarkDeceased(props.match.params.id)} 
                />
            )}

            {isError && <ErrorMessage 
                message={tCommon('errorLoadingData')} 
                title={tCommon('errorLoadingDataTitle')} 
                errorDetails={errorMessages} 
                options={[{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} 
            />}

            {props.saver.state === FetchState.Busy && !isError && <SavingMessage entity={tClient('client')} />}
            {props.saver.state === FetchState.Error && !isError && <ErrorMessage 
                title={tCommon('savingErrorTitle')} 
                message={tCommon('savingErrorXMessage', {x: props.saver.errorMessage})} 
                canClose 
                options={[]} 
                errorDetails={JSON.stringify(saveData)} 
            />}

            {props.deleter.state === FetchState.Busy && !isError && <DeletingMessage entity={tClient('client')} />}
            {props.deleter.state === FetchState.Error && !isError && <ErrorMessage 
                title={tCommon('deletingErrorTitle')} 
                message={tCommon('deletingErrorXMessage', {x: props.deleter.errorMessage})} 
                canClose 
                options={[]} 
                errorDetails={JSON.stringify(details)} 
            />}

            {props.markDeceasedItem.state === FetchState.Busy && !isError && <SavingMessage entity={tClient('client')} />}
            {props.markDeceasedItem.state === FetchState.Error && !isError && <ErrorMessage 
                title={tCommon('savingErrorTitle')} 
                message={tCommon('savingErrorXMessage', {x: props.markDeceasedItem.errorMessage})} 
                canClose 
                options={[]} 
                errorDetails={props.markDeceasedItem.errorMessage} 
            />}

            {props.unmarkDeceasedItem.state === FetchState.Busy && !isError && <SavingMessage entity={tClient('client')} />}
            {props.unmarkDeceasedItem.state === FetchState.Error && !isError && <ErrorMessage 
                title={tCommon('savingErrorTitle')} 
                message={tCommon('savingErrorXMessage', {x: props.unmarkDeceasedItem.errorMessage})} 
                canClose 
                options={[]} 
                errorDetails={props.unmarkDeceasedItem.errorMessage} 
            />}
        </ClientNavigation>
    );
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(ClientEditPage);

